import memoized from "../../utils/memoized";
import * as R from "ramda";

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
}

function ne(a, b) {
  return a !== b;
}

function eq(a, b) {
  return a === b;
}

/**
 * @param {string} str
 * @param {string|function} fnOrString
 */
export function indexOrInvoke(str, fnOrString) {
  switch (typeof fnOrString) {
    case "string":
      let f = fnOrString.indexOf("!") !== -1 ? eq : ne;
      let s = f === eq ? fnOrString.slice(1) : fnOrString;
      return f(str.toLowerCase().indexOf(s.toLowerCase()), -1);
    case "function":
      return fnOrString(str);
    default:
      throw new Error(`Argument 2 of indexOrInvoke is invalid ${typeof fnOrString}`);
  }
}

const COMPILATION_REF = "#__#";

// NOTE: Operators must all be of the same length.
export const OR = "|";
export const AND = "&";

const OPERATOR_MAP = {
  [OR]: (a, b) => str => indexOrInvoke(str, a) || indexOrInvoke(str, b),
  [AND]: (a, b) => str => indexOrInvoke(str, a) && indexOrInvoke(str, b)
};

const OPERATORS = Object.keys(OPERATOR_MAP).reduce((acc, op) => [...acc, op], []);
const OPERATOR_LENGTH = OPERATORS[0].length;

let OPERATOR_REGEX = new RegExp("(" + OPERATORS.map(escapeRegExp).join("|") + ")");
let REPEATED_OPERATOR_REGEX = new RegExp(OPERATOR_REGEX + "+", "g");
let PADDED_OPERATOR_REGEX = new RegExp("\\s*[" + OPERATORS.map(escapeRegExp).join("") + "]\\s*");

const COMPILATION_STATE = {
  stackCount: 0,
  stack: [],
  fragments: [],
  compiled: [],
  add(range) {
    let ref = `${COMPILATION_REF}${this.stackCount++}`;
    this.stack.push(range);
    return ref;
  },
  pop() {
    if (this.stackCount === 0) return;
    this.stackCount--;
    return this.stack.shift();
  },
  setReady(fragment) {
    this.fragments.push(fragment);
  },
  reset() {
    this.stack = [];
    this.compiled = [];
    this.fragments = [];
    this.stackCount = 0;
  }
};

/**
 * @param {string} str
 * @param {number} index
 * @param {number} end
 * @param {string} add
 * @returns {string}
 */
function spliceSlice(str, index, end, add) {
  return str.slice(0, index) + add + str.slice(end);
}

/**
 * @param {string} srcFragment
 */
function parseFragment(srcFragment) {
  // Checking Args
  if (typeof srcFragment !== "string") throw new Error("Filter must be a string.");

  // Pulling operators
  const operators = [];
  for (let i = 0; i < srcFragment.length - OPERATOR_LENGTH; i++) {
    const c = srcFragment.substring(i, i + OPERATOR_LENGTH);
    if (OPERATOR_REGEX.test(c)) operators.push(c);
  }

  // Creating tokens
  const tokens = srcFragment
    .replace(REPEATED_OPERATOR_REGEX, "$1")
    .split(PADDED_OPERATOR_REGEX)
    .filter(s => s !== "");

  return { operators, tokens, srcFragment };
}

function getReference(arg) {
  if (arg.indexOf && arg.indexOf(`${COMPILATION_REF}`) !== -1) {
    let argFn = COMPILATION_STATE.compiled[parseInt(arg.split(`${COMPILATION_REF}`)[1])];
    if (!argFn) throw new Error("Could not find compiled fragment.");
    return argFn;
  } else {
    return arg;
  }
}

/**
 * @returns {Function}
 */
function compileFragment({ operators = [], tokens = [], srcFragment }) {
  // If no operators or invalid program structure than just use indexOf.
  if (operators.length === 0 || operators.length + 1 !== tokens.length)
    return string => indexOrInvoke(string, srcFragment);

  // create program
  while (tokens.length > 0 && operators.length > 0) {
    // Check if left and/or right are references.
    let right = getReference(tokens.pop());
    let left = getReference(tokens.pop());

    tokens.push(OPERATOR_MAP[operators.pop()](left, right));
  }

  // The program lives in the first index.
  return string => indexOrInvoke(string, tokens[0]);
}

/**
 * @param {string} src
 * @returns {Array<number>}
 */
export function scanForInnerMostParens(src) {
  const firstParent = src.indexOf("(");

  if (firstParent === -1) return [-1, -1];

  for (let s = firstParent; s < src.length; s++) {
    for (let e = s + 1; e < src.length; e++) {
      if (src[e] === "(") {
        s = e;
      } else if (src[e] === ")") {
        return [s, e];
      }
    }
  }

  return [-1, -1];
}

/**
 * Builds a function from a string... Is this kinda like an interpreter?
 * @param {string} filter source
 */
function createCompilationFragments(filter) {
  let [s, e] = scanForInnerMostParens(filter);
  if (OPERATOR_REGEX.test(filter) && s == -1 && e == -1) {
    COMPILATION_STATE.add(filter);
  }
  if (s == -1 && e == -1) return;

  let ref = COMPILATION_STATE.add(filter.substring(s + 1, e));

  createCompilationFragments(spliceSlice(filter, s, e + 1, ref));
}

// NOTE: This function needs more work.
/**
 * @param {string} filter
 * @returns {string}
 */
export function sanitizeFilter(filter) {
  let t = /\(([\w\s]+)\)/; // Test for items with no operators inside.
  filter = filter
    .replace(/(\!\s*)/, "!")
    .replace(new RegExp(`${escapeRegExp(AND)}+`, "g"), AND)
    .replace(new RegExp(`${escapeRegExp(OR)}+`, "g"), OR);

  while (t.test(filter)) {
    filter = filter.replace(t, "$1");
  }
  return filter;
}

const wrap = (start = `"`, end) => {
  if (end == null) end = start;
  return str => `${start}${str}${end}`;
};

function handleMultipleConditions(filter) {
  return filter
    .split(/\s*,\s*/g)
    .map(wrap("(", ")"))
    .join(' | ');
}

/**
 * Creates a function that checks a string for patterns.
 * createLogicFilter("a | b") -> str => or("a", "b")(str)
 * @param {string} filter - The source of the filter.
 * @returns {function}
 */
function createLogicFilter(filter) {
  filter = filter.toString();

  filter = handleMultipleConditions(filter);
  // NOTE: I know these don't match up logically. Talk to Reuben.
  //       BTW, I'm not sure how I feel about the location of this code.
  filter = filter
    .replace(/(\s*)(not|NOT)(\s+)/g, "$1!$3")
    .replace(/(\s+(or|OR)\s+)/g, OR)
    .replace(/(\s+(and|AND)\s+)/g, AND);

  // if (!OPERATOR_REGEX.test(filter))
  //   return str => new RegExp(escapeRegExp(`(${filter.split(/\s+/).join("|")})`)).test(str);

  filter = sanitizeFilter(filter);
  createCompilationFragments(filter);

  if (COMPILATION_STATE.stackCount === 0) {
    return string => indexOrInvoke(string, filter);
  }

  while (COMPILATION_STATE.stackCount > 0) {
    let fragment = COMPILATION_STATE.pop();
    COMPILATION_STATE.setReady(parseFragment(fragment));
  }

  for (const ready of COMPILATION_STATE.fragments) {
    COMPILATION_STATE.compiled.push(compileFragment(ready));
  }

  let result = COMPILATION_STATE.compiled[COMPILATION_STATE.compiled.length - 1];
  COMPILATION_STATE.reset();
  return result;
}

createLogicFilter = memoized({
  toString: args =>
    args
      .toString()
      .toLowerCase()
      .replace(/\s+/, " "),
  handler: createLogicFilter
});

export default createLogicFilter;
